import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, TrendingUp, FileText, ShoppingCart, Wallet,
  ArrowRight, Calculator, AlertTriangle, CheckCircle, Clock,
  Building, User, Filter, RefreshCw, PiggyBank, Percent
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FactoringMarketplace = () => {
  const [activeTab, setActiveTab] = useState('marketplace'); // marketplace, sell, portfolio
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userType, setUserType] = useState(''); // carrier or investor
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Marketplace state
  const [listings, setListings] = useState([]);
  const [marketStats, setMarketStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  
  // Sell invoice form
  const [sellForm, setSellForm] = useState({
    debtorName: '',
    invoiceAmount: '',
    discountRate: '5',
    dueDate: '',
    description: ''
  });
  
  // Portfolio state
  const [carrierData, setCarrierData] = useState(null);
  const [investorData, setInvestorData] = useState(null);
  
  // Calculator
  const [calcAmount, setCalcAmount] = useState('10000');
  const [calcDiscount, setCalcDiscount] = useState('5');
  const [calcResult, setCalcResult] = useState(null);

  useEffect(() => {
    fetchMarketplace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMarketplace = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (minAmount) params.append('min_amount', minAmount);
      if (maxAmount) params.append('max_amount', maxAmount);
      if (maxDiscount) params.append('max_discount', parseFloat(maxDiscount) / 100);
      
      const res = await axios.get(`${API}/factoring/marketplace?${params.toString()}`);
      setListings(res.data.listings || []);
      setMarketStats(res.data.marketplace_stats);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!userEmail || !userName || !userType) {
      toast.error('Please fill all fields');
      return;
    }
    
    setIsLoggedIn(true);
    
    // Fetch portfolio data based on user type
    if (userType === 'carrier') {
      try {
        const res = await axios.get(`${API}/factoring/carrier/${encodeURIComponent(userEmail)}`);
        setCarrierData(res.data);
      } catch (error) {
        console.error('Error:', error);
      }
    } else {
      try {
        const res = await axios.get(`${API}/factoring/investor/${encodeURIComponent(userEmail)}`);
        setInvestorData(res.data);
      } catch (error) {
        console.error('Error:', error);
      }
    }
    
    toast.success(`Welcome, ${userName}!`);
  };

  const handleSellInvoice = async () => {
    if (!sellForm.debtorName || !sellForm.invoiceAmount || !sellForm.discountRate) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/factoring/list`, {
        carrier_email: userEmail,
        carrier_name: userName,
        debtor_name: sellForm.debtorName,
        invoice_amount: parseFloat(sellForm.invoiceAmount),
        discount_rate: parseFloat(sellForm.discountRate) / 100,
        due_date: sellForm.dueDate || undefined,
        description: sellForm.description || undefined
      });
      
      toast.success(res.data.message);
      setSellForm({ debtorName: '', invoiceAmount: '', discountRate: '5', dueDate: '', description: '' });
      fetchMarketplace();
      
      // Refresh carrier data
      const carrierRes = await axios.get(`${API}/factoring/carrier/${encodeURIComponent(userEmail)}`);
      setCarrierData(carrierRes.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to list invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (listing) => {
    if (!isLoggedIn) {
      toast.error('Please log in as an investor first');
      return;
    }
    
    if (userType !== 'investor') {
      toast.error('Only investors can purchase invoices');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/factoring/purchase`, {
        listing_id: listing.id,
        investor_email: userEmail,
        investor_name: userName
      });
      
      toast.success(res.data.message);
      fetchMarketplace();
      
      // Refresh investor data
      const invRes = await axios.get(`${API}/factoring/investor/${encodeURIComponent(userEmail)}`);
      setInvestorData(invRes.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase invoice');
    }
  };

  const calculateFactoring = async () => {
    try {
      const res = await axios.get(`${API}/factoring/calculator`, {
        params: {
          invoice_amount: parseFloat(calcAmount),
          discount_rate: parseFloat(calcDiscount) / 100
        }
      });
      setCalcResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation error');
    }
  };

  const getRiskBadge = (risk) => {
    const colors = {
      LOW: 'bg-green-500/20 text-green-400',
      MEDIUM: 'bg-amber-500/20 text-amber-400',
      HIGH: 'bg-red-500/20 text-red-400'
    };
    return colors[risk?.level] || colors.MEDIUM;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-7xl mx-auto" data-testid="factoring-marketplace-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <PiggyBank className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Invoice Factoring Marketplace</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Carriers: Sell your pending invoices for immediate cash. Investors: Buy invoices at a discount for guaranteed returns.
          </p>
        </div>

        {/* Login Section */}
        {!isLoggedIn ? (
          <Card className="bg-slate-800/50 border-slate-700 max-w-md mx-auto mb-8" data-testid="factoring-login">
            <CardHeader>
              <CardTitle className="text-white">Get Started</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Your email"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
              <Input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Your name / Company name"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setUserType('carrier')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'carrier' 
                      ? 'border-emerald-500 bg-emerald-500/20' 
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <User className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-white font-medium">I&apos;m a Carrier</p>
                  <p className="text-xs text-gray-400">Sell invoices</p>
                </button>
                <button
                  onClick={() => setUserType('investor')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'investor' 
                      ? 'border-blue-500 bg-blue-500/20' 
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <Building className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-white font-medium">I&apos;m an Investor</p>
                  <p className="text-xs text-gray-400">Buy invoices</p>
                </button>
              </div>
              <Button 
                onClick={handleLogin}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500"
                data-testid="factoring-login-btn"
              >
                Enter Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* User Info Bar */}
            <div className="flex justify-between items-center mb-6 p-4 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  userType === 'carrier' ? 'bg-emerald-500/20' : 'bg-blue-500/20'
                }`}>
                  {userType === 'carrier' ? 
                    <User className="w-5 h-5 text-emerald-400" /> : 
                    <Building className="w-5 h-5 text-blue-400" />
                  }
                </div>
                <div>
                  <p className="text-white font-medium">{userName}</p>
                  <p className="text-sm text-gray-400 capitalize">{userType}</p>
                </div>
              </div>
              <Button variant="ghost" onClick={() => setIsLoggedIn(false)} className="text-gray-400">
                Switch Account
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              {['marketplace', userType === 'carrier' ? 'sell' : null, 'portfolio', 'calculator'].filter(Boolean).map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'default' : 'outline'}
                  onClick={() => setActiveTab(tab)}
                  className={activeTab === tab ? 'bg-emerald-500' : ''}
                >
                  {tab === 'marketplace' && <ShoppingCart className="w-4 h-4 mr-2" />}
                  {tab === 'sell' && <FileText className="w-4 h-4 mr-2" />}
                  {tab === 'portfolio' && <Wallet className="w-4 h-4 mr-2" />}
                  {tab === 'calculator' && <Calculator className="w-4 h-4 mr-2" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Button>
              ))}
            </div>
          </>
        )}

        {/* Marketplace Tab */}
        {(activeTab === 'marketplace' || !isLoggedIn) && (
          <div className="space-y-6">
            {/* Market Stats */}
            {marketStats && (
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{formatCurrency(marketStats.total_available_value)}</p>
                    <p className="text-sm text-gray-400">Available for Purchase</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{marketStats.total_listings}</p>
                    <p className="text-sm text-gray-400">Active Listings</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <Percent className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{marketStats.average_discount_rate}%</p>
                    <p className="text-sm text-gray-400">Avg. Discount Rate</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Min Amount</label>
                    <Input
                      type="number"
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                      placeholder="$0"
                      className="bg-slate-700/50 border-slate-600 text-white w-32"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Max Amount</label>
                    <Input
                      type="number"
                      value={maxAmount}
                      onChange={(e) => setMaxAmount(e.target.value)}
                      placeholder="$100,000"
                      className="bg-slate-700/50 border-slate-600 text-white w-32"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Max Discount %</label>
                    <Input
                      type="number"
                      value={maxDiscount}
                      onChange={(e) => setMaxDiscount(e.target.value)}
                      placeholder="15%"
                      className="bg-slate-700/50 border-slate-600 text-white w-32"
                    />
                  </div>
                  <Button onClick={fetchMarketplace} className="bg-emerald-500">
                    <Filter className="w-4 h-4 mr-2" /> Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Listings */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
                </div>
              ) : listings.length === 0 ? (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No invoices available</p>
                    <p className="text-sm text-gray-500">Be the first to list an invoice!</p>
                  </CardContent>
                </Card>
              ) : (
                listings.map((listing) => (
                  <Card key={listing.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskBadge(listing.risk_score)}`}>
                              {listing.risk_score?.level} RISK
                            </span>
                            <span className="text-gray-500 text-sm">•</span>
                            <span className="text-gray-400 text-sm">{listing.days_until_due} days to due</span>
                          </div>
                          <p className="text-white font-medium text-lg">Debtor: {listing.debtor_name}</p>
                          <p className="text-gray-400 text-sm">Carrier: {listing.carrier_name}</p>
                          {listing.description && (
                            <p className="text-gray-500 text-sm mt-1">{listing.description}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-gray-400 text-xs">Invoice Value</p>
                            <p className="text-xl font-bold text-white">{formatCurrency(listing.invoice_amount)}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-500" />
                          <div className="text-center">
                            <p className="text-gray-400 text-xs">You Pay</p>
                            <p className="text-xl font-bold text-emerald-400">{formatCurrency(listing.purchase_price)}</p>
                          </div>
                          <div className="text-center bg-green-500/10 p-3 rounded-lg">
                            <p className="text-green-400 text-xs">Your Profit</p>
                            <p className="text-xl font-bold text-green-400">+{formatCurrency(listing.discount_amount)}</p>
                            <p className="text-green-300 text-xs">{listing.discount_rate * 100}% ROI</p>
                          </div>
                          {isLoggedIn && userType === 'investor' && (
                            <Button 
                              onClick={() => handlePurchase(listing)}
                              className="bg-gradient-to-r from-emerald-500 to-teal-500"
                              data-testid={`purchase-btn-${listing.id}`}
                            >
                              <ShoppingCart className="w-4 h-4 mr-2" /> Purchase
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Sell Invoice Tab (Carriers only) */}
        {activeTab === 'sell' && isLoggedIn && userType === 'carrier' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-500" />
                  List Invoice for Sale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Debtor Name (Who owes you) *</label>
                  <Input
                    value={sellForm.debtorName}
                    onChange={(e) => setSellForm({...sellForm, debtorName: e.target.value})}
                    placeholder="e.g., ABC Logistics Inc."
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Invoice Amount *</label>
                  <Input
                    type="number"
                    value={sellForm.invoiceAmount}
                    onChange={(e) => setSellForm({...sellForm, invoiceAmount: e.target.value})}
                    placeholder="$10,000"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Discount Rate (%) *</label>
                  <Input
                    type="number"
                    min="2"
                    max="15"
                    value={sellForm.discountRate}
                    onChange={(e) => setSellForm({...sellForm, discountRate: e.target.value})}
                    placeholder="5"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Min 2%, Max 15%. Higher discount = faster sale.</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
                  <Input
                    type="date"
                    value={sellForm.dueDate}
                    onChange={(e) => setSellForm({...sellForm, dueDate: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Description (optional)</label>
                  <Input
                    value={sellForm.description}
                    onChange={(e) => setSellForm({...sellForm, description: e.target.value})}
                    placeholder="Load details, reference numbers..."
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <Button 
                  onClick={handleSellInvoice}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500"
                  data-testid="list-invoice-btn"
                >
                  {loading ? 'Listing...' : 'List Invoice for Sale'}
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30">
              <CardHeader>
                <CardTitle className="text-white">Instant Quote</CardTitle>
              </CardHeader>
              <CardContent>
                {sellForm.invoiceAmount && sellForm.discountRate ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-gray-400">Invoice Amount</span>
                      <span className="text-white font-bold">{formatCurrency(parseFloat(sellForm.invoiceAmount))}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-gray-400">Discount ({sellForm.discountRate}%)</span>
                      <span className="text-red-400">-{formatCurrency(parseFloat(sellForm.invoiceAmount) * (parseFloat(sellForm.discountRate) / 100))}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-gray-400">Platform Fee (1.5%)</span>
                      <span className="text-amber-400">-{formatCurrency(parseFloat(sellForm.invoiceAmount) * (1 - parseFloat(sellForm.discountRate) / 100) * 0.015)}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-emerald-500/20 rounded-lg border border-emerald-500/50">
                      <span className="text-emerald-300 font-medium">You Receive</span>
                      <span className="text-2xl font-bold text-emerald-400">
                        {formatCurrency(
                          parseFloat(sellForm.invoiceAmount) * 
                          (1 - parseFloat(sellForm.discountRate) / 100) * 
                          (1 - 0.015)
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 text-center">
                      Get cash in 24-48 hours instead of waiting 30+ days!
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Enter invoice details to see your quote</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && isLoggedIn && (
          <div className="space-y-6">
            {userType === 'carrier' && carrierData && (
              <>
                {/* Carrier Stats */}
                <div className="grid sm:grid-cols-4 gap-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{carrierData.stats?.total_listed || 0}</p>
                      <p className="text-sm text-gray-400">Total Listed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{carrierData.stats?.currently_available || 0}</p>
                      <p className="text-sm text-gray-400">Available</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{carrierData.stats?.sold || 0}</p>
                      <p className="text-sm text-gray-400">Sold</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <DollarSign className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{formatCurrency(carrierData.stats?.total_cash_received)}</p>
                      <p className="text-sm text-gray-400">Cash Received</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Carrier Listings */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Your Listed Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {carrierData.listings?.length === 0 ? (
                      <p className="text-gray-400 text-center py-4">No invoices listed yet</p>
                    ) : (
                      <div className="space-y-3">
                        {carrierData.listings?.map((listing) => (
                          <div key={listing.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                            <div>
                              <p className="text-white font-medium">{listing.debtor_name}</p>
                              <p className="text-sm text-gray-400">{formatCurrency(listing.invoice_amount)}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              listing.status === 'sold' ? 'bg-green-500/20 text-green-400' :
                              listing.status === 'available' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {listing.status?.toUpperCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {userType === 'investor' && investorData && (
              <>
                {/* Investor Stats */}
                <div className="grid sm:grid-cols-4 gap-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <ShoppingCart className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{investorData.stats?.total_purchases || 0}</p>
                      <p className="text-sm text-gray-400">Purchases</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{formatCurrency(investorData.stats?.total_invested)}</p>
                      <p className="text-sm text-gray-400">Total Invested</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{formatCurrency(investorData.stats?.total_profit_realized)}</p>
                      <p className="text-sm text-gray-400">Profit Realized</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <Percent className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{investorData.stats?.avg_roi || 0}%</p>
                      <p className="text-sm text-gray-400">Avg. ROI</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Investor Purchases */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Your Purchased Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {investorData.purchases?.length === 0 ? (
                      <p className="text-gray-400 text-center py-4">No purchases yet</p>
                    ) : (
                      <div className="space-y-3">
                        {investorData.purchases?.map((purchase) => (
                          <div key={purchase.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                            <div>
                              <p className="text-white font-medium">From: {purchase.carrier_name}</p>
                              <p className="text-sm text-gray-400">
                                Paid: {formatCurrency(purchase.purchase_price)} | Expected: {formatCurrency(purchase.invoice_amount)}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                purchase.status === 'collected' ? 'bg-green-500/20 text-green-400' :
                                purchase.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                                {purchase.status?.toUpperCase()}
                              </span>
                              <p className="text-green-400 text-sm mt-1">+{formatCurrency(purchase.expected_profit)} profit</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && isLoggedIn && (
          <div className="max-w-xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-emerald-500" />
                  Factoring Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Invoice Amount</label>
                  <Input
                    type="number"
                    value={calcAmount}
                    onChange={(e) => setCalcAmount(e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Discount Rate (%)</label>
                  <Input
                    type="number"
                    min="2"
                    max="15"
                    value={calcDiscount}
                    onChange={(e) => setCalcDiscount(e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <Button onClick={calculateFactoring} className="w-full bg-emerald-500">
                  Calculate
                </Button>

                {calcResult && (
                  <div className="mt-6 space-y-4 p-4 bg-slate-700/30 rounded-lg">
                    <h4 className="text-white font-medium border-b border-slate-600 pb-2">Results</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Invoice Amount</p>
                        <p className="text-white font-medium">{formatCurrency(calcResult.calculations?.invoice_amount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Discount Amount</p>
                        <p className="text-red-400 font-medium">-{formatCurrency(calcResult.calculations?.discount_amount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Platform Fee</p>
                        <p className="text-amber-400 font-medium">-{formatCurrency(calcResult.calculations?.platform_fee)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Carrier Receives</p>
                        <p className="text-emerald-400 font-bold">{formatCurrency(calcResult.calculations?.carrier_receives)}</p>
                      </div>
                    </div>
                    <div className="border-t border-slate-600 pt-4 mt-4">
                      <p className="text-gray-400 text-sm mb-2">For Investors:</p>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Purchase Price</span>
                        <span className="text-white">{formatCurrency(calcResult.calculations?.purchase_price)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Expected Profit</span>
                        <span className="text-green-400 font-bold">+{formatCurrency(calcResult.calculations?.investor_profit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">ROI</span>
                        <span className="text-green-400 font-bold">{calcResult.calculations?.investor_roi_percent}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        {!isLoggedIn && (
          <Card className="bg-slate-800/50 border-slate-700 mt-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                How Invoice Factoring Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" /> For Carriers
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-400 text-sm font-bold">1</span>
                      </div>
                      <p className="text-gray-300 text-sm">List your pending invoice with a discount rate (2-15%)</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-400 text-sm font-bold">2</span>
                      </div>
                      <p className="text-gray-300 text-sm">An investor purchases your invoice</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-400 text-sm font-bold">3</span>
                      </div>
                      <p className="text-gray-300 text-sm">Get cash immediately (minus discount & 1.5% fee)</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-blue-400 font-bold mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5" /> For Investors
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 text-sm font-bold">1</span>
                      </div>
                      <p className="text-gray-300 text-sm">Browse available invoices and review risk scores</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 text-sm font-bold">2</span>
                      </div>
                      <p className="text-gray-300 text-sm">Purchase invoices at a discount (e.g., $9,500 for $10,000)</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 text-sm font-bold">3</span>
                      </div>
                      <p className="text-gray-300 text-sm">Collect full amount when debtor pays (2-15% ROI)</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FactoringMarketplace;
